<template lang="pug">
    .table-properties-panel(v-if="table")
        h3 {{ $t('Table Properties') }}

        el-form(label-position="top" size="small")
            el-form-item(:label="$t('Name')")
                el-input(v-model="localName" @change="updateField('name', localName)" :disabled="!canUpdate")

            el-form-item(:label="$t('Number')")
                el-input(v-model="localNumber" @change="updateField('number', localNumber)" :disabled="!canUpdate")

            el-form-item(:label="$t('Shape')")
                el-radio-group(v-model="localShape" @change="updateField('shape', localShape)" :disabled="!canUpdate")
                    el-radio-button(label="round") {{ $t('Round') }}
                    el-radio-button(label="square") {{ $t('Rectangular') }}

            el-form-item(:label="$t('Seats')")
                el-input-number(v-model="localSeats" :min="1" :max="20" @change="updateField('seats', localSeats)" style="width: 100%" :disabled="!canUpdate")

            el-form-item(:label="$t('Seat Sides')" v-if="localShape === 'square'")
                .seat-sides-editor
                    .seat-sides-editor__cell
                        span.seat-sides-editor__label {{ $t('Top') }}
                        el-input-number(v-model="localSeatSides.top" :min="0" :max="seatSideMax.top" size="mini" @change="updateSeatSides" :disabled="!canUpdate")
                    .seat-sides-editor__middle
                        .seat-sides-editor__cell
                            span.seat-sides-editor__label {{ $t('Left') }}
                            el-input-number(v-model="localSeatSides.left" :min="0" :max="seatSideMax.left" size="mini" @change="updateSeatSides" :disabled="!canUpdate")
                        .seat-sides-editor__cell
                            span.seat-sides-editor__label {{ $t('Right') }}
                            el-input-number(v-model="localSeatSides.right" :min="0" :max="seatSideMax.right" size="mini" @change="updateSeatSides" :disabled="!canUpdate")
                    .seat-sides-editor__cell
                        span.seat-sides-editor__label {{ $t('Bottom') }}
                        el-input-number(v-model="localSeatSides.bottom" :min="0" :max="seatSideMax.bottom" size="mini" @change="updateSeatSides" :disabled="!canUpdate")

            el-form-item(:label="$t('Width') + ' (px)'")
                el-slider(v-model="localWidth" :min="40" :max="250" @change="updateField('width', localWidth)" :disabled="!canUpdate")

            el-form-item(:label="$t('Height') + ' (px)'")
                el-slider(v-model="localHeight" :min="40" :max="250" @change="updateField('height', localHeight)" :disabled="!canUpdate")

            el-form-item(:label="$t('Rotation') + ' (°)'")
                el-slider(v-model="localRotation" :min="0" :max="360" :step="5" @change="updateField('rotation', localRotation)" :disabled="!canUpdate")

            el-form-item(:label="$t('Status')")
                el-tag(:type="statusColor" size="small" effect="plain") {{ statusLabel }}

            el-form-item(:label="$t('Part of combinations')" v-if="tableCombinations.length")
                .table-combinations-tags
                    el-tag(
                        v-for="combination in tableCombinations"
                        :key="combination.id"
                        size="mini"
                        type="info"
                        effect="plain"
                    ) {{ combination.name || $t('Unnamed') }}

            el-divider

            .panel-actions
                el-button(
                    v-if="!table.blocked && canUpdate"
                    size="small"
                    type="warning"
                    @click="$emit('block')"
                ) {{ $t('Block Table') }}

                el-button(
                    v-else-if="canUpdate"
                    size="small"
                    type="success"
                    @click="$emit('unblock')"
                ) {{ $t('Unblock Table') }}

                el-button(
                    v-if="canDelete"
                    size="small"
                    type="danger"
                    @click="$emit('delete')"
                ) {{ $t('Delete Table') }}
</template>

<i18n>
{
    "de": {
        "Table Properties": "Tisch-Eigenschaften",
        "Name": "Name",
        "Number": "Nummer",
        "Shape": "Form",
        "Round": "Rund",
        "Rectangular": "Eckig",
        "Seats": "Sitzplätze",
        "Width": "Breite",
        "Height": "Höhe",
        "Rotation": "Drehung",
        "Status": "Status",
        "Block Table": "Tisch blockieren",
        "Unblock Table": "Tisch freigeben",
        "Delete Table": "Tisch löschen",
        "Seat Sides": "Sitzseiten",
        "Top": "Oben",
        "Bottom": "Unten",
        "Left": "Links",
        "Right": "Rechts",
        "Part of combinations": "Teil von Kombinationen",
        "Unnamed": "Unbenannt"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useTableReservationFloorPlanStore } from '@/stores';

const statusLabelsMap = {
    available: 'Verfügbar',
    reserved: 'Reserviert',
    occupied: 'Besetzt',
    blocked: 'Blockiert'
};

const statusColorsMap = {
    available: 'success',
    reserved: 'warning',
    occupied: 'danger',
    blocked: 'info'
};

export default {
    name: 'TablePropertiesPanel',
    props: {
        table: {
            type: Object,
            required: true
        },
        status: {
            type: String,
            default: 'available'
        },
        canDelete: {
            type: Boolean,
            default: false
        },
        canUpdate: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            localName: '',
            localNumber: '',
            localShape: 'round',
            localSeats: 2,
            localWidth: 80,
            localHeight: 80,
            localRotation: 0,
            localSeatSides: { top: 2, bottom: 2, left: 0, right: 0 }
        };
    },
    computed: {
        floorPlanStore: function () {
            return useTableReservationFloorPlanStore();
        },
        tableCombinations: function () {
            if (!this.table) return [];
            return this.floorPlanStore.combinationsForTable(this.table.id);
        },
        statusLabel: function () {
            return statusLabelsMap[this.status] || this.status;
        },
        statusColor: function () {
            return statusColorsMap[this.status] || '';
        },
        seatSideMax: function () {
            const s = this.localSeatSides;
            const total = this.localSeats;
            return {
                top: Math.max(0, total - (s.bottom || 0) - (s.left || 0) - (s.right || 0)),
                bottom: Math.max(0, total - (s.top || 0) - (s.left || 0) - (s.right || 0)),
                left: Math.max(0, total - (s.top || 0) - (s.bottom || 0) - (s.right || 0)),
                right: Math.max(0, total - (s.top || 0) - (s.bottom || 0) - (s.left || 0))
            };
        }
    },
    watch: {
        table: {
            immediate: true,
            handler: function (val) {
                if (val) {
                    this.localName = val.name;
                    this.localNumber = val.number;
                    this.localShape = val.shape;
                    this.localSeats = val.seats;
                    this.localWidth = val.width;
                    this.localHeight = val.height;
                    this.localRotation = val.rotation;
                    this.localSeatSides = val.seatSides
                        ? _.cloneDeep(val.seatSides)
                        : { top: 2, bottom: 2, left: 0, right: 0 };
                }
            }
        }
    },
    methods: {
        updateField: function (field, value) {
            const data = {};
            data[field] = value;
            this.$emit('update', data);
        },
        updateSeatSides: function () {
            this.updateField('seatSides', _.cloneDeep(this.localSeatSides));
        }
    }
};
</script>

<style lang="sass" scoped>
.table-properties-panel
    h3
        margin-top: 0
        margin-bottom: 15px
        font-size: 1rem
        color: #303133

.panel-actions
    margin-top: 10px
    display: flex
    flex-direction: column
    gap: 8px

    ::v-deep .el-button
        display: block
        width: 100%
        margin: 0

.seat-sides-editor
    display: flex
    flex-direction: column
    align-items: center
    gap: 6px

    &__middle
        display: flex
        align-items: center
        justify-content: center
        gap: 8px
        width: 100%

    &__cell
        display: flex
        flex-direction: column
        align-items: center
        gap: 2px

    &__label
        font-size: 0.7rem
        color: #909399

    ::v-deep .el-input-number
        width: 80px

.table-combinations-tags
    display: flex
    flex-wrap: wrap
    gap: 4px
</style>
